<script setup lang="ts">
import {
  type DisplayStrategy,
  type DropdownDrawerTypes,
  type PlacementTypes,
  type WidthStrategy,
} from "@/types";
import { BREAKPOINTS } from "@/consts";
import { useBreakpoints } from "@vueuse/core";
import Button from "@/stories/buttons/Button/Button.vue";
import { computed, ref, toRefs, watch, type StyleValue } from "vue";
import { useElementRect } from "@/composables/useElementRect";

export type ContentOverflow = "overflow-auto" | "overflow-hidden";

interface Props {
  widthStrategy: WidthStrategy;
  drawer?: DropdownDrawerTypes;
  title?: string;
  placement?: PlacementTypes;
  fullWidth?: boolean;
  classes?: string;
  customStyle: Partial<StyleValue>;
  saveButtonKey?: string;
  saveButtonDisplayment?: DisplayStrategy;
  contentOverflow?: ContentOverflow;
}

const props = withDefaults(defineProps<Props>(), {
  drawer: "desktop",
  placement: "left",
  fullWidth: false,
  saveButtonKey: "global.save",
  saveButtonDisplayment: "auto",
  contentOverflow: "overflow-auto",
});

const emit = defineEmits<{
  click: [MouseEvent];
  cancel: [];
  save: [];
  onContentHeight: [number];
}>();

const { drawer, saveButtonDisplayment, widthStrategy } = toRefs(props);
const breakpoints = useBreakpoints(BREAKPOINTS);
const isTablet = breakpoints.greaterOrEqual("tablet");
const contentRef = ref<Element | null>(null);
const contentRect = useElementRect(contentRef);

const isMobileDrawer = computed(() => {
  if (drawer.value === "desktop") {
    return false;
  }

  if (drawer.value === "mobile") {
    return true;
  }

  return !isTablet.value;
});

const showSaveButton = computed(() => {
  if (saveButtonDisplayment.value === "auto") {
    return isMobileDrawer.value;
  }

  return saveButtonDisplayment.value === "show";
});

watch(
  () => contentRect.value,
  () => {
    emit("onContentHeight", contentRect.value?.height || 300);
  },
);
</script>

<template>
  <div
    v-if="isMobileDrawer"
    class="bg-black opacity-50 fixed top-0 left-0 w-full h-full z-10"
  ></div>
  <div
    class="bg-white p-2 flex flex-col gap-1 z-50 h-fit shadow-large min-w-[10rem] max-h-[50rem] t-dropdown-menu-container"
    :class="{
      'absolute rounded-lg border border-neutral100 ': !isMobileDrawer,
      'fixed bottom-0 left-0 w-full max-h-[93dvh] rounded-tl-3xl rounded-tr-3xl border border-neutral300':
        isMobileDrawer,
      'border border-neutral200 rounded-lg': !isMobileDrawer,
      'w-fit': widthStrategy === 'fit',
      [classes || '']: true,
    }"
    :style="customStyle"
    @click="(event) => $emit('click', event)"
  >
    <div
      v-if="isMobileDrawer"
      class="relative flex justify-center items-center h500 text-neutral1000 py-4 border-b border-neutral300 -ml-2 -mr-2"
      :class="[isMobileDrawer ? 'mt-0' : 'py-4']"
    >
      <div
        v-if="title"
        class="w-full text-center"
        :class="[isMobileDrawer ? 'mx-14 truncate' : '']"
      >
        {{ title }}
      </div>
      <button
        type="button"
        class="absolute right-5 text-neutral1000 w-10 h-10"
        @click="emit('cancel')"
        :full-width="fullWidth"
      >
        <i class="fas fa-times text-2xl" />
      </button>
    </div>
    <div class="p-[0.125rem]" ref="contentRef" :class="[contentOverflow]">
      <slot></slot>
    </div>
    <div
      v-if="showSaveButton"
      class="border-t border-neutral300 -ml-2 -mr-2 py-3 pb-4 px-4 dropdown-trigger-no-close"
    >
      <Button
        :text="$t(saveButtonKey)"
        class="whitespace-nowrap"
        full-width
        @click="$emit('save')"
      />
    </div>
  </div>
</template>
